import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  createInstruction,
  deleteInstruction,
  getPropertyInstructions,
  updateInstruction,
} from 'api/instructions';
import {
  ActionFunction,
  useFetcher,
  useLoaderData,
  useLocation,
  useOutletContext,
  useRevalidator,
} from 'react-router';
import {
  EntityPanelActionHandler,
  FormMode,
  Property,
  PropertyInstruction,
  TraxeroActionResponse,
} from 'types';
import {
  Box,
  ListItem,
  ListItemIcon,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext } from 'context/appContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { useFetcherKey } from 'hooks/useFetcherKey';
import { PanelRootContext } from 'types/PanelRootContext';
import FormFrame from 'components/layout/FormFrame';
import { getBlankInstruction } from 'utils/emptyEntities';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import {
  useDefaultTableOptions,
  TableType,
} from 'hooks/useDefaultTableOptions';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import MobilePanelList from 'components/lists/MobilePanelList';
import { TAB_LAYOUT_HEADER_HEIGHT } from 'components/layout/FormTabNavigation';
import { ListItemMenuButton } from 'components/buttons/ListItemMenuButton';
import { InstructionDialog } from './InstructionDialog';

export interface InstructionsLoaderData {
  _instructions: Promise<PropertyInstruction[]>;
}

export interface InstructionsRouteParams {
  params: {
    tenantID?: string | undefined;
    propertyID?: string | undefined;
  };
}

export const instructionsLoader = ({ params }: InstructionsRouteParams) => {
  return {
    _instructions: getPropertyInstructions({
      tenantID: params.tenantID,
      propertyID: params.propertyID,
    }),
  };
};

export const instructionsAction: ActionFunction = async ({
  params,
  request,
}): Promise<TraxeroActionResponse<EntityPanelActionHandler>> => {
  const { body, handler } = await request.json();
  const { tenantID, propertyID } = params as InstructionsRouteParams['params'];
  let res: Response;

  try {
    switch (handler) {
      case EntityPanelActionHandler.PROPERTY_CREATE:
        res = await createInstruction({
          tenantID,
          propertyID,
          body: JSON.stringify(body),
        });
        break;
      case EntityPanelActionHandler.UPDATE:
        res = await updateInstruction({
          tenantID,
          propertyID,
          instructionID: body.id,
          body: JSON.stringify(body),
        });
        break;
      case EntityPanelActionHandler.DELETE:
        res = await deleteInstruction({
          tenantID,
          propertyID,
          instructionID: body.id,
        });
        break;
      default:
        res = new Response(null, {
          status: 404,
          statusText: 'Unknown Handler',
        });
    }
  } catch (e) {
    res = new Response(null, { status: 500, statusText: 'Network Error' });
  }

  return {
    handler,
    status: res.status,
    statusText: res.statusText,
  };
};

const MAX_INSTRUCTIONS = 10;

const renderImageCell = ({
  row,
}: {
  row: { original: PropertyInstruction };
}): ReactNode => {
  const url = row.original.url;
  if (!url) return null;
  return (
    <Box
      component='img'
      src={url}
      alt=''
      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
      sx={{
        maxHeight: 250,
        maxWidth: '100%',
        objectFit: 'contain',
        borderRadius: 1,
      }}
    />
  );
};

export const InstructionsPanel = (): ReactNode => {
  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const { _instructions } = useLoaderData() as InstructionsLoaderData;
  const { state } = useContext(AppContext);
  const { entity } = useOutletContext<PanelRootContext<Property>>();
  const displayToast = useContext(ToastContext);

  const canEdit = state.entityPermissions.rule.update;

  const [instructions, setInstructions] = useState<PropertyInstruction[]>([]);
  const [currentInstruction, setCurrentInstruction] =
    useState<PropertyInstruction | null>(null);
  const [formMode, setFormMode] = useState<FormMode>('none');

  // Loader data resolution
  useEffect(() => {
    const awaitLoaderData = async () => {
      const data = await _instructions;
      setInstructions(data);
    };
    awaitLoaderData();
  }, [_instructions]);

  // Fetcher + response handling
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const { pathname } = useLocation();
  const revalidator = useRevalidator();

  useEffect(() => {
    if (!fetcher.data) return;
    const response =
      fetcher.data as TraxeroActionResponse<EntityPanelActionHandler>;
    if (response.status < 400) {
      const msgMap: Record<string, string> = {
        [EntityPanelActionHandler.PROPERTY_CREATE]: 'Created',
        [EntityPanelActionHandler.UPDATE]: 'Updated',
        [EntityPanelActionHandler.DELETE]: 'Deleted',
      };
      displayToast({
        type: TOAST_TYPE.SUCCESS,
        message: `${msgMap[response.handler] ?? ''} ${t('instruction.label', {
          ns: 'entities',
        })}`,
      });
      revalidator.revalidate();
      handleCloseDialog();
    } else {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: `Error: ${response.statusText}`,
      });
    }
  }, [fetcher.data]);

  const isAtLimit = instructions.length >= MAX_INSTRUCTIONS;

  const handleCreateClick = () => {
    setFormMode('create');
    setCurrentInstruction(getBlankInstruction());
  };

  const handleItemClick = (item: PropertyInstruction) => {
    setFormMode('edit');
    setCurrentInstruction({ ...item });
  };

  const handleCloseDialog = () => {
    setFormMode('none');
    setCurrentInstruction(null);
  };

  const handleDialogSubmit = (fileKey: string) => {
    if (!currentInstruction) return;
    if (formMode === 'create') {
      fetcher.submit(
        {
          handler: EntityPanelActionHandler.PROPERTY_CREATE,
          body: {
            title: currentInstruction.title,
            text: currentInstruction.text,
            fileKey,
          },
        },
        { method: 'POST', action: pathname, encType: 'application/json' },
      );
    } else {
      fetcher.submit(
        {
          handler: EntityPanelActionHandler.UPDATE,
          body: {
            id: currentInstruction.id,
            title: currentInstruction.title,
            text: currentInstruction.text,
            fileKey,
          },
        },
        { method: 'PUT', action: pathname, encType: 'application/json' },
      );
    }
  };

  const handleDelete = useCallback(
    (item: PropertyInstruction) => {
      fetcher.submit(
        {
          handler: EntityPanelActionHandler.DELETE,
          body: { id: item.id },
        },
        { method: 'DELETE', action: pathname, encType: 'application/json' },
      );
    },
    [fetcher, pathname],
  );

  const columns = useMemo<MRT_ColumnDef<PropertyInstruction>[]>(
    () => [
      {
        header: t('instruction.title', { ns: 'entities' }),
        accessorKey: 'title',
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            backgroundColor: 'white',
          },
        },
      },
      {
        header: t('instruction.image', { ns: 'entities' }),
        accessorKey: 'url',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: renderImageCell,
      },
      {
        header: t('instruction.text', { ns: 'entities' }),
        accessorKey: 'text',
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            backgroundColor: 'white',
          },
        },
      },
    ],
    [t],
  );

  const tableDefaults = useDefaultTableOptions<PropertyInstruction>(
    TableType.NESTED,
  );
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: instructions,
    columns,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'compact',
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading: fetcher.state !== 'idle',
      showLoadingOverlay: false,
    },
    getRowId: (row) => `${row.id}`,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        cursor: canEdit ? 'pointer' : 'default',
        width: '100%',
        height: 200,
      },
      onClick: () => {
        if (canEdit) {
          handleItemClick(row.original);
        }
      },
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        isSubmitting={fetcher.state === 'submitting'}
        optionsDisabled={!canEdit}
        tableType={TableType.NESTED}
        hideColumnFilters={true}
        hideHideShowColumns={true}
        hideSearch={true}
        hideFullScreen={true}
        menuOptions={[
          {
            onClick: handleCreateClick,
            content: t('buttons.create', {
              name: t('instruction.label', { ns: 'entities' }),
            }),
            disabled: !canEdit || isAtLimit,
            icon: <Add />,
          },
        ]}
      >
        <Stack
          direction={'row'}
          flexGrow={1}
          justifyContent={'flex-start'}
          alignItems={'center'}
        >
          <Typography variant='h3' textTransform={'capitalize'}>
            {t('instruction.label_plural', { ns: 'entities' })}
          </Typography>
        </Stack>
      </MRTableToolbar>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      <MenuItem
        key='edit'
        onClick={() => {
          handleItemClick(row.original);
          closeMenu();
        }}
        sx={{ m: 0, backgroundColor: 'white' }}
        disabled={!canEdit}
      >
        <ListItemIcon>
          <Edit />
        </ListItemIcon>
        {t('buttons.update', {
          name: t('instruction.label', { ns: 'entities' }),
        })}
      </MenuItem>,
      <MenuItem
        key='delete'
        divider
        onClick={() => {
          handleDelete(row.original);
          closeMenu();
        }}
        sx={{ m: 0, backgroundColor: 'white' }}
        disabled={!canEdit}
      >
        <ListItemIcon>
          <Delete />
        </ListItemIcon>
        {t('buttons.delete', {
          name: t('instruction.label', { ns: 'entities' }),
        })}
      </MenuItem>,
    ],
  });

  const instructionLabel = t('instruction.label', { ns: 'entities' });

  if (mobile) {
    const itemHeight = 300;
    const renderItem = (instruction: PropertyInstruction): ReactNode => (
      <ListItem
        key={instruction.id}
        id={instruction.id.toString()}
        onClick={() => handleItemClick(instruction)}
        divider
        secondaryAction={
          <ListItemMenuButton
            menuOptions={[
              {
                onClick: () => handleItemClick(instruction),
                content: t('buttons.update', { name: instructionLabel }),
                disabled: !canEdit,
              },
              {
                onClick: () => handleDelete(instruction),
                content: t('buttons.delete', { name: instructionLabel }),
                disabled: !canEdit,
              },
            ]}
          />
        }
        sx={{
          height: itemHeight,
          backgroundColor: 'white',
          alignItems: 'flex-start',
          py: 1.5,
          pr: 6,
        }}
      >
        <Stack
          direction='column'
          spacing={1}
          sx={{ width: '100%', height: '100%', overflow: 'hidden' }}
        >
          <Typography
            variant='subtitle1'
            fontWeight={600}
            sx={{ wordBreak: 'break-word' }}
          >
            {instruction.title || instructionLabel}
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {instruction.url && (
              <Box
                component='img'
                src={instruction.url}
                alt={instruction.title || instructionLabel}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
                sx={{
                  maxHeight: 200,
                  maxWidth: '100%',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            )}
          </Box>
          <Typography
            variant='subtitle2'
            color='text.secondary'
            sx={{
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 10,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {instruction.text}
          </Typography>
        </Stack>
      </ListItem>
    );

    return (
      <>
        {currentInstruction && (
          <InstructionDialog
            open={!!currentInstruction}
            instruction={currentInstruction}
            setInstruction={setCurrentInstruction}
            formMode={formMode}
            handleCancel={handleCloseDialog}
            handleSubmit={handleDialogSubmit}
            entity={entity}
          />
        )}
        <MobilePanelList<PropertyInstruction>
          data={instructions}
          columns={columns.map((c) => ({
            name: c.header,
            searchKey: c.accessorKey as string,
          }))}
          renderItem={renderItem}
          title={t('instruction.label_plural', { ns: 'entities' })}
          itemHeight={itemHeight}
          withSearchBar
          heightOffset={TAB_LAYOUT_HEADER_HEIGHT}
          menuOptions={[
            {
              onClick: handleCreateClick,
              content: t('buttons.create', { name: instructionLabel }),
              disabled: !canEdit || isAtLimit,
              icon: <Add />,
            },
          ]}
        />
      </>
    );
  }

  return (
    <FormFrame>
      {currentInstruction && (
        <InstructionDialog
          open={!!currentInstruction}
          instruction={currentInstruction}
          setInstruction={setCurrentInstruction}
          formMode={formMode}
          handleCancel={handleCloseDialog}
          handleSubmit={handleDialogSubmit}
          entity={entity}
        />
      )}
      <MaterialReactTable table={table} />
    </FormFrame>
  );
};
